/**
 * MentionRedditContextCard Component
 *
 * Displays Reddit context information for a mention (date, author, subreddit, links)
 * Shared component used by both MentionReviewDetailPage and BusinessMentionCard
 */
import React from 'react';
import { Card, Stack, Group, Text, Anchor, Button } from '@mantine/core';
import { IconClock, IconUser, IconBrandReddit, IconExternalLink } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { formatRedditDate } from './mentionReviewHelpers';
import { buildPostReviewDetailRoute } from '@/presentation/routing/routes';
import { appendRedditContext } from '@/utils/redditUtils';

export interface MentionRedditContextCardProps {
  /**
   * When the post/comment was created on Reddit
   */
  postCreatedAt: string;

  /**
   * Reddit username (optional)
   */
  redditAuthor: string | null;

  /**
   * Subreddit name
   */
  subredditName: string;

  /**
   * Reddit permalink URL (optional)
   */
  redditPermalink: string | null;

  /**
   * Reddit post ID (optional)
   */
  redditPostId: string | null;

  /**
   * Whether to show the card border
   */
  withBorder?: boolean;

  /**
   * Card padding
   */
  padding?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Card shadow
   */
  shadow?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * MentionRedditContextCard - Reusable Reddit context display
 */
export const MentionRedditContextCard: React.FC<MentionRedditContextCardProps> = ({
  postCreatedAt,
  redditAuthor,
  subredditName,
  redditPermalink,
  redditPostId,
  withBorder = true,
  padding = 'md',
  shadow = 'sm'
}): React.ReactElement => {
  return (
    <Card withBorder={withBorder} padding={padding} shadow={shadow}>
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Reddit Context
        </Text>

        {/* Posted on Reddit (user's timezone) */}
        {postCreatedAt && (
          <Group gap="xs">
            <IconClock size={16} />
            <Text size="xs" c="dimmed">
              Posted on Reddit: {formatRedditDate(postCreatedAt)}
            </Text>
          </Group>
        )}

        {/* Reddit Author */}
        {redditAuthor && (
          <Group gap="xs">
            <IconUser size={16} />
            <Text size="xs" c="dimmed">
              By: u/{redditAuthor}
            </Text>
          </Group>
        )}

        {/* Subreddit */}
        <Group gap="xs">
          <IconBrandReddit size={16} />
          <Text size="xs" c="dimmed">
            Subreddit: r/{subredditName}
          </Text>
        </Group>

        {/* Links */}
        <Group gap="md" wrap="wrap">
          {redditPermalink && (
            <Anchor href={appendRedditContext(redditPermalink)} target="_blank" rel="noopener noreferrer" size="sm">
              View on Reddit →
            </Anchor>
          )}
          {redditPostId && (
            <Button
              component={Link}
              to={buildPostReviewDetailRoute(redditPostId)}
              variant="light"
              size="sm"
              leftSection={<IconExternalLink size={16} />}
            >
              View Post Review
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
};
